import moment from "moment";
import { splitLines } from "./buildLicenseFeeSlideData";
import {
  formatSuiteNumbersList,
  sumDefaultSeatsFromSuites,
} from "utils/suiteDisplayUtils";

const DEFAULT_CUSTOMISED =
  "Completely customised space with meeting rooms, pantry, phone booths, collaboration areas, hot desks, Wi-Fi and utilities.";

function workstationCount(row) {
  if (!row) return null;
  const direct = Number(row.proposedWorkstation ?? row.workstations);
  if (Number.isFinite(direct) && direct > 0) return direct;
  if (row.spaceUpdate === "WholeSuite") {
    const seats = sumDefaultSeatsFromSuites(row);
    if (seats > 0) return seats;
  }
  return null;
}

/** Top line + bottom label for lease term box (matches static template layout). */
export function formatLeaseTermBox(row) {
  const m = Number(row?.contractTerm);
  if (!Number.isFinite(m) || m <= 0) {
    return { primary: "—", label: "Lease term" };
  }
  if (m % 12 === 0) {
    const y = m / 12;
    return {
      primary: `${y} year${y === 1 ? "" : "s"}`,
      label: "Lease term",
    };
  }
  return { primary: String(m), label: "months Lease term" };
}

/** Top line + bottom label for unit price box. */
export function formatUnitPriceBox(row, wsCount) {
  const rpw = Number(row?.ratePerWorkstation);
  if (Number.isFinite(rpw) && rpw > 0) {
    return {
      primary: `S$${rpw.toLocaleString()}`,
      label: "per workstation",
    };
  }
  const monthly = Number(row?.monthlyRental);
  if (
    Number.isFinite(wsCount) &&
    wsCount > 0 &&
    Number.isFinite(monthly) &&
    monthly > 0
  ) {
    const per = Math.round(monthly / wsCount);
    return {
      primary: `S$${per.toLocaleString()}`,
      label: "per workstation",
    };
  }
  if (Number.isFinite(monthly) && monthly > 0) {
    return {
      primary: `S$${monthly.toLocaleString()}`,
      label: "per month",
    };
  }
  return { primary: "—", label: "per workstation" };
}

function pleaseNoteDepositLine(row) {
  if (!row) return "—";
  const sd = Number(row.securityDeposit);
  const mr = Number(row.monthlyRental);
  if (!Number.isFinite(sd) || sd <= 0) {
    return "Security deposit as per agreement";
  }
  if (
    sd <= 24 &&
    sd === Math.floor(sd) &&
    Number.isFinite(mr) &&
    sd * mr > 0
  ) {
    return `${sd} month(s) deposit (S$ ${Number(sd * mr).toLocaleString()})`;
  }
  return `S$ ${sd.toLocaleString()} security deposit`;
}

function customisedSpaceLine(row) {
  const lines = splitLines(row?.requirementBreakup);
  const joined = lines.join(" ").trim();
  if (joined) return joined;
  return DEFAULT_CUSTOMISED;
}

/**
 * Payload for SW “Pricing” slide (preview + PDF). Mirrors template: PROPOSAL header, pricing grid, Please note list.
 */
export function buildSwPricingSlideData(row) {
  if (!row) return null;

  const ws = workstationCount(row);
  const workstationsPrimary = ws != null ? String(ws) : "—";

  const lease = formatLeaseTermBox(row);
  const unit = formatUnitPriceBox(row, ws);

  const monthly = Number(row.monthlyRental);
  const totalMonthlyLine =
    Number.isFinite(monthly) && monthly > 0
      ? `SGD ${monthly.toLocaleString()}/month`
      : "—";

  const suiteList = formatSuiteNumbersList(row);
  const suitePrimary = suiteList ? suiteList : "—";

  const validityLine = row.proposalValidity
    ? `Proposal valid till ${moment(row.proposalValidity).format("Do MMMM YYYY")}`
    : "";

  const pleaseNoteItems = [
    pleaseNoteDepositLine(row),
    customisedSpaceLine(row),
    validityLine || "Proposal validity as per agreement",
  ];

  return {
    workstationsPrimary,
    workstationsLabel: "Workstations",
    suitePrimary,
    suiteLabel: "Suite no.",
    leasePrimary: lease.primary,
    leaseLabel: lease.label,
    unitPrimary: unit.primary,
    unitLabel: unit.label,
    totalMonthlyLine,
    pleaseNoteItems,
  };
}
